import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import {
  DisplayError,
  ModalItem,
  ModalPageView,
} from '@cotera/client/app/components/app';
import { AskResponse } from '../../actions/framework/types';
import { GRAPH_ACTION_GROUP } from '../actions/constants';
import { Expression, From, SQL, Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { DG } from '@cotera/sdk/core';
import { CsvUploadModel } from './csv-upload.model';
import { TabManager } from '../../tab-manager';
import { Routes } from '../../routes';
import { ConfigureTable } from './configure-table';
import { Suspense } from 'react';
import { Center, Loading } from '@cotera/client/app/components/ui';
import { ErrorBoundary } from 'react-error-boundary';

type TableInfo = {
  table_name: string;
  table_schema: string;
};

type ColumnInfo = {
  table_name: string;
  table_schema: string;
  column_name: string;
  data_type: string;
  is_nullable: boolean;
};

export type CsvUploadParams = {
  preferredDatasetName: string;
  uploadedDbTableName: string;
  tableInfoSchemas: TableInfo[];
  columnInfoSchemas: ColumnInfo[];
  dialect: SQL.SqlDialect;
  refetchInfoSchemas: () => Promise<{
    tableInfoSchemas: TableInfo[];
    columnInfoSchemas: ColumnInfo[];
  }>;
};

export type CsvUploadResult = {
  headerRow: string[];
  dataRows: Ty.Scalar[][];
};

export type CsvUploadError =
  | { t: 'empty-csv-file' }
  | { t: 'exceeded-max-rows'; message: string }
  | { t: 'parse-error'; message: string }
  | { t: 'create-table-error' };

type ModalManager = {
  push: (
    child: ModalItem & {
      onClose?: () => void;
    }
  ) => void;
  close: () => void;
};

export class ConfigureTableAction extends BaseAction<
  CsvUploadParams,
  CsvUploadError[]
> {
  title = 'Upload CSV';
  description = 'Configure the table name and types for a new dataset';
  inputSchema = z.object({
    dialect: z.custom<SQL.SqlDialect>(),
    preferredDatasetName: z.string(),
    uploadedDbTableName: z.string(),
    tableInfoSchemas: z.array(
      z.object({
        table_name: z.string(),
        table_schema: z.string(),
      })
    ),
    columnInfoSchemas: z.array(
      z.object({
        table_name: z.string(),
        table_schema: z.string(),
        column_name: z.string(),
        data_type: z.string(),
        is_nullable: z.boolean(),
      })
    ),
    refetchInfoSchemas: z.custom<CsvUploadParams['refetchInfoSchemas']>(),
  });
  group = GRAPH_ACTION_GROUP;
  icon = 'upload';
  discrete = true;

  constructor(
    private readonly modalManager: ModalManager,
    private readonly tabManager: Pick<TabManager, 'pushAndReplace'>
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.isInScope(GraphModel) && context.isInScope(CsvUploadModel);
  }

  async execute(
    params: CsvUploadParams,
    context: ExecuteContext
  ): Promise<ActionResult<CsvUploadError[]>> {
    const model = context.getInScopeModelOfType(CsvUploadModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    Assert.assert(graphModel !== null, 'GraphModel not found');
    Assert.assert(model !== null, 'CsvUploadModel not found');

    const { dialect, preferredDatasetName, uploadedDbTableName } = params;

    let { tableInfoSchemas: freshTables, columnInfoSchemas: freshColumns } =
      params;

    let table = freshTables.find((v) => v.table_name === uploadedDbTableName);

    // Retry with backoff if the table is not found immediately
    const MAX_RETRIES = 2;
    const BACKOFF_MS = [2000, 4000]; // 2s, then 4s

    for (let attempt = 1; attempt <= MAX_RETRIES && !table; attempt++) {
      model.state.set({
        t: 'verifying-table',
        attempt,
      });

      await sleep(BACKOFF_MS[attempt - 1]!);

      const refetched = await params.refetchInfoSchemas();
      freshTables = refetched.tableInfoSchemas;
      freshColumns = refetched.columnInfoSchemas;

      table = freshTables.find((v) => v.table_name === uploadedDbTableName);
    }

    if (!table) {
      model.state.set({
        t: 'await-upload',
        errors: [{ t: 'create-table-error' }],
      });
      return ok({
        next: true,
      });
    }

    const columns = freshColumns.filter(
      (v) => v.table_name === uploadedDbTableName
    );

    const attributes = Object.fromEntries(
      columns.map(({ column_name, data_type }) => [
        column_name,
        safelyMakeEraType(dialect, data_type) ?? Ty.ty('super'),
      ])
    );

    const baseRelation = From({
      name: table.table_name,
      schema: table.table_schema,
      attributes,
    });

    const startingEraQl = baseRelation.toEraQL();

    // The primary key expression references the __row_number column
    const pkExpr = Expression.fromAst({
      t: 'attr',
      name: '__row_number',
      source: 'from',
      ty: Ty.ty('int'),
    });

    const stagingResult = await graphModel.attemptToStageOperations([
      DG.Operations.createRel({
        symbol: preferredDatasetName,
        source: startingEraQl,
        pk: pkExpr.toEraQL(),
      }),
    ]);

    if (stagingResult.isErr()) {
      return err([{ t: 'create-table-error' }]);
    }

    this.tabManager.pushAndReplace({
      route: Routes.dataset.forId(preferredDatasetName).toString(),
    });

    model.reset();

    return ok({});
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  ask(
    _: Partial<CsvUploadParams>,
    context: ExecuteContext
  ): AskResponse<CsvUploadParams> {
    const model = context.getInScopeModelOfType(CsvUploadModel);
    Assert.assert(model !== null, 'CsvUploadModel not found');

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <ModalPageView
              title="Configure Table"
              subtitle="Configure the table name and types for a new dataset"
              className="px-4 py-4"
            >
              <ErrorBoundary
                fallbackRender={({ error }) => <DisplayError error={error} />}
              >
                <Suspense
                  fallback={
                    <Center>
                      <Loading.Dots />
                    </Center>
                  }
                >
                  <ConfigureTable
                    initialName={model.tentativeFile.snapshot()?.name ?? ''}
                    headerRow={model.headerRow.snapshot()}
                    dataRows={model.dataRows.snapshot()}
                    onUpload={async (params) => {
                      await onRespond(params);
                      this.modalManager.close();
                    }}
                  />
                </Suspense>
              </ErrorBoundary>
            </ModalPageView>
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const safelyMakeEraType = (dialect: SQL.SqlDialect, type: string) => {
  try {
    return dialect.tryWarehouseTypeToEraType(type);
  } catch {
    return null;
  }
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
